import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  CircularProgress, 
  Alert,
  Button,
  Paper
} from '@mui/material';
import { AuditioBackendManager, AudioFile } from './AuditioBackend';

interface AudioFileHandlerProps {
  audioFileId: number;
  onFileLoaded: (file: AudioFile) => void;
  onError: (error: string) => void;
}

const AudioFileHandler: React.FC<AudioFileHandlerProps> = ({ 
  audioFileId, 
  onFileLoaded, 
  onError 
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasErrored, setHasErrored] = useState(false);
  const [backendManager] = useState(() => new AuditioBackendManager());

  useEffect(() => {
    // Reset error state when audioFileId changes
    setHasErrored(false);
    setError(null);
    setLoading(true);
  }, [audioFileId]);

  useEffect(() => {
    // Don't retry if we've already encountered an error for this file ID
    if (hasErrored) {
      return;
    }

    const loadAudioFile = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('🔄 Loading audio file with ID:', audioFileId);
        
        const audioFile = await backendManager.getAudioFileById(audioFileId);
        
        if (!audioFile) {
          const errorMessage = 'Sorry, this file no longer exists! Please try another id.';
          console.error('❌ Audio file not found:', errorMessage);
          setError(errorMessage);
          setHasErrored(true);
          onError(errorMessage);
          return; // Don't continue, stay in error state
        }

        console.log('✅ Audio file loaded successfully:', {
          id: audioFile.id,
          fileName: audioFile.fileName,
          snippetsCount: audioFile.snippets?.length || 0
        });

        onFileLoaded(audioFile);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load audio file';
        console.error('❌ Error loading audio file:', errorMessage);
        setError(errorMessage);
        setHasErrored(true);
        onError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    loadAudioFile();
  }, [audioFileId, backendManager]); // Removed onFileLoaded and onError from dependencies

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        gap="20px"
        p={4}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Loading audio file...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        gap="20px"
        p={4}
      >
        <Paper sx={{ p: 3, maxWidth: 500, textAlign: 'center' }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            The file you're looking for may have been deleted or the ID might be incorrect.
          </Typography>
          <Button 
            variant="contained" 
            onClick={() => window.location.href = '/'}
          >
            Go to Home
          </Button>
        </Paper>
      </Box>
    );
  }

  return null;
};

export default AudioFileHandler; 